"use client";

/**
 * MIN-293 — `AgentChat.Root`, purely controlled. Relocated from
 * `apps/provider/src/components/agent-chat/root.tsx`'s `NoThreadProvider`/
 * `ThreadProvider` internals, with the store-backed uncontrolled branch
 * deleted: `threadId`/`onThreadChange` are both required now, so there is no
 * caller-vs-store merge left for the package to own (`resolveActiveThreadId`
 * goes with it — see `apps/provider`'s own thin wrapper, which re-derives
 * that rule against `agentActiveThreadStore` instead).
 *
 * Takes `auth`/`baseUrl` explicitly rather than pulling them off a host SDK
 * (`useMedplum`, a router) — the package boundary test forbids any
 * `@medplum/*` import, and the host already has both values on hand (its own
 * query definitions need them too).
 */
import { useChat } from "@ai-sdk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useMemo, useRef, useState } from "react";
import { useCreateThread, useDeleteThread } from "../agent-chat-provider";
import type { ChatRouteContext } from "../chat-context";
import type { ChatAuth } from "../chat-fetch";
import { nextThreadAfterDelete, sortChatThreadsByCreatedAt } from "../chat-thread-order";
import type { CreateThreadInput } from "../create-agent-chat";
import { uiToBubbles } from "../elements/chat-turn";
import { mergePartRenderers, type PartRenderers } from "../elements/render-turn-part";
import { chatThreadQueries } from "../queries";
import { evictThreadChat } from "../registry";
import { resolveThreadConversation } from "../thread-conversation";
import { useThreadChat } from "../use-thread-chat";
import { AgentChatContext, type AgentChatContextValue } from "./context";

const NO_ROUTE_CONTEXT: () => ChatRouteContext = () => ({});

export interface AgentChatRootProps {
  children: ReactNode;
  /** The thread to show. `null` forces the empty new-chat composer,
   * regardless of anything a host app might otherwise remember. */
  threadId: string | null;
  /** Fires whenever the compound's own actions change which thread is
   * current: a new thread's first message, or deleting the thread on
   * screen (which lands on its neighbor — see `nextThreadAfterDelete`). */
  onThreadChange: (threadId: string | null) => void;
  auth: ChatAuth;
  baseUrl: string;
  onAfterSubmit?: () => void;
  /** Read at send time, not build time — pass the same closure every
   * render. Defaults to an empty context when the host has none to add. */
  getRouteContext?: () => ChatRouteContext;
  /** Overrides the house `PartRenderers` for `Turn` — merged so an unnamed
   * kind keeps rendering the house way (see `mergePartRenderers`). */
  renderers?: Partial<PartRenderers>;
}

export function AgentChatRoot({
  children,
  threadId,
  onThreadChange,
  auth,
  baseUrl,
  onAfterSubmit,
  getRouteContext = NO_ROUTE_CONTEXT,
  renderers,
}: AgentChatRootProps) {
  const resolvedRenderers = useMemo(() => mergePartRenderers(renderers), [renderers]);

  if (threadId === null) {
    return (
      <NoThreadProvider
        auth={auth}
        baseUrl={baseUrl}
        getRouteContext={getRouteContext}
        onAfterSubmit={onAfterSubmit}
        onThreadChange={onThreadChange}
        renderers={resolvedRenderers}
      >
        {children}
      </NoThreadProvider>
    );
  }
  // Keyed so a thread switch resets the composer's local state (draft,
  // focus) — safe because the conversation itself lives in the chat
  // registry rather than in this mount, so remounting neither discards nor
  // re-fetches it.
  return (
    <ThreadProvider
      auth={auth}
      baseUrl={baseUrl}
      getRouteContext={getRouteContext}
      key={threadId}
      onAfterSubmit={onAfterSubmit}
      onThreadChange={onThreadChange}
      renderers={resolvedRenderers}
      threadId={threadId}
    >
      {children}
    </ThreadProvider>
  );
}

// ─── thread provider ──────────────────────────────────────────────────────

interface ThreadProviderProps {
  children: ReactNode;
  threadId: string;
  auth: ChatAuth;
  baseUrl: string;
  onAfterSubmit?: () => void;
  onThreadChange: (threadId: string | null) => void;
  getRouteContext: () => ChatRouteContext;
  renderers: PartRenderers;
}

function ThreadProvider({
  children,
  threadId,
  auth,
  baseUrl,
  onAfterSubmit,
  onThreadChange,
  getRouteContext,
  renderers,
}: ThreadProviderProps) {
  const { data: threads } = useQuery(chatThreadQueries.list(auth, baseUrl));
  const { chat, seedPending } = useThreadChat(threadId, { auth, baseUrl, getRouteContext });
  const { messages, sendMessage, status, stop } = useChat({ chat });
  const { mutateAsync: createThreadAsync } = useCreateThread();
  const { mutateAsync: deleteThreadAsync } = useDeleteThread();

  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Nothing to show and no history yet: hold the composer and show the
  // ellipsis. Once the Chat holds messages — hydrated, or streaming in from
  // another surface — a seed query still in flight is not a loading state.
  const isSeeding = seedPending && messages.length === 0;
  const isStreaming = status === "streaming" || status === "submitted";
  const isBusy = isSeeding || isStreaming;

  const bubbles = useMemo(() => uiToBubbles(messages), [messages]);
  const trimmed = draft.trim();
  const subjectPatientId = threads?.find((t) => t.id === threadId)?.subjectPatientId ?? null;

  const value = useMemo<AgentChatContextValue>(
    () => ({
      state: {
        messages: bubbles,
        draft,
        canSubmit: !!trimmed && !isBusy,
        isStreaming: isBusy,
        hasActiveThread: true,
        activeThreadId: threadId,
        subjectPatientId,
      },
      actions: {
        setDraft,
        // `overrides` (model/systemPromptId) is ignored here — an established
        // thread already fixed both at creation, and preserving that today's
        // behaviour rather than "fixing" it (the audit's "selectorsLocked,
        // then discarded" note) is this issue's explicit scope.
        submit: () => {
          if (!trimmed || isBusy) return;
          sendMessage({ text: trimmed });
          setDraft("");
          onAfterSubmit?.();
        },
        stop: () => {
          if (!isBusy) return;
          stop();
        },
        newChat: async () => {
          const t = await createThreadAsync({});
          onThreadChange(t.id);
          inputRef.current?.focus();
        },
        deleteChat: async () => {
          await deleteThreadAsync(threadId);
          // Drop the conversation with the thread, so a recycled id can
          // never inherit it and no surface keeps rendering a deleted chat.
          evictThreadChat(threadId);
          const ordered = sortChatThreadsByCreatedAt(threads ?? []).map((t) => t.id);
          onThreadChange(nextThreadAfterDelete(ordered, threadId, threadId));
          inputRef.current?.focus();
        },
      },
      meta: { inputRef, renderers },
    }),
    [
      bubbles,
      draft,
      trimmed,
      isBusy,
      subjectPatientId,
      sendMessage,
      stop,
      onAfterSubmit,
      createThreadAsync,
      deleteThreadAsync,
      threadId,
      threads,
      onThreadChange,
      renderers,
    ],
  );

  return <AgentChatContext value={value}>{children}</AgentChatContext>;
}

// ─── empty-state provider ─────────────────────────────────────────────────

interface NoThreadProviderProps {
  children: ReactNode;
  auth: ChatAuth;
  baseUrl: string;
  onAfterSubmit?: () => void;
  onThreadChange: (threadId: string) => void;
  getRouteContext: () => ChatRouteContext;
  renderers: PartRenderers;
}

function NoThreadProvider({
  children,
  auth,
  baseUrl,
  onAfterSubmit,
  onThreadChange,
  getRouteContext,
  renderers,
}: NoThreadProviderProps) {
  const queryClient = useQueryClient();
  const { mutateAsync: createThreadAsync, isPending: isCreatingThread } = useCreateThread();
  const [draft, setDraft] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const trimmed = draft.trim();

  const value = useMemo<AgentChatContextValue>(
    () => ({
      state: {
        messages: [],
        draft,
        canSubmit: !!trimmed && !isCreatingThread,
        isStreaming: false,
        hasActiveThread: false,
        activeThreadId: null,
        subjectPatientId: null,
      },
      actions: {
        setDraft,
        // The only path that actually creates a thread — this is where a
        // `Settings` provider's chosen model (if any) reaches the creation
        // body (MIN-294's "choosing a model changes what the next thread is
        // created with").
        submit: (overrides) => {
          if (!trimmed || isCreatingThread) return;
          setDraft("");
          // No title: naming a thread is the engine's job. It generates a
          // 4-to-6-word summary from the first exchange, but only for a thread
          // whose name is still null (`services/ai-engine/src/api/chat/service.py`
          // — `if thread.title is None`), so seeding the first message here (as
          // this did until MIN-316) silently turned that off and left every
          // thread named with a verbatim user message. Until the engine's name
          // lands, history surfaces show `TITLE_PENDING` — see
          // `../chat-thread-title`. A caller that genuinely knows a thread's
          // name can still pass one through the client (`CreateThreadInput`
          // keeps the field); what is gone is deriving one from the draft.
          const seed: CreateThreadInput = {};
          if (overrides?.model) seed.model = overrides.model;
          createThreadAsync(seed).then(
            (t) => {
              // Send straight into the new thread's own Chat. The message
              // used to be parked in a global store for whichever provider
              // mounted first to pick up — with several surfaces mounted,
              // more than one could, and it went twice.
              resolveThreadConversation(t.id, { auth, baseUrl, queryClient, getRouteContext }).sendMessage({
                text: trimmed,
              });
              onThreadChange(t.id);
            },
            // Create failed — hand the text back rather than swallow it.
            () => setDraft(trimmed),
          );
          onAfterSubmit?.();
        },
        // No thread yet, so nothing can be streaming — always a no-op.
        stop: () => {},
        newChat: () => {
          // Already empty — just refocus.
          inputRef.current?.focus();
        },
        deleteChat: () => {
          // Nothing to delete.
        },
      },
      meta: { inputRef, renderers },
    }),
    [
      draft,
      trimmed,
      isCreatingThread,
      createThreadAsync,
      auth,
      baseUrl,
      queryClient,
      getRouteContext,
      onAfterSubmit,
      onThreadChange,
      renderers,
    ],
  );

  return <AgentChatContext value={value}>{children}</AgentChatContext>;
}
